import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PurchaseHeader from '../components/manualPurchase/PurchaseHeader';
import PaymentDateTime from '../components/manualPurchase/PaymentDateTime';
import {
  paymentWallTime,
  PURCHASE_TIME_ZONE,
  timeZoneLabel,
} from '../utils/purchaseTime';
import PurchaseSummary, {
  PurchaseError,
} from '../components/manualPurchase/PurchaseSummary';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';
import service from '../services/manualPurchaseService';
import PurchaseModal from '../components/manualPurchase/PurchaseModal';
import OrderSummary from '../components/manualPurchase/OrderSummary';
import EvidenceViewer from '../components/manualPurchase/EvidenceViewer';
import EvidenceLightbox from '../components/manualPurchase/EvidenceLightbox';
import {
  buildPurchaseInput,
  calculatePurchasePrice,
  clearPurchaseIntent,
  formatPurchaseMoney,
  isPurchaseFulfilled,
  limitPurchaseDiscount,
  purchaseQuantityError,
  readPurchaseIntent,
  savePurchaseIntent,
  validateEvidence,
} from '../utils/manualPurchase';

const inputClass = 'purchase-input w-full';
const primaryClass =
  'rounded-md bg-blue-600 px-4 py-[10px] text-base text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryClass =
  'rounded-md border border-gray-400 px-4 py-[9px] text-base hover:bg-gray-50 disabled:opacity-50';
const freshForm = () => {
  const now = new Date();
  return {
    quantity: '',
    discountType: '',
    discountValue: '',
    reference: '',
    paymentAt: paymentWallTime(now),
    timeZone: timeZoneLabel(PURCHASE_TIME_ZONE, now),
    paymentOffset: '',
  };
};
const detailPath = id =>
  ROUTES.generatePath(ROUTES.MANUAL_PURCHASE_DETAIL, { id });

const ManualPurchaseForm = ({ userId }) => {
  const navigate = useNavigate();
  const [initial] = useState(() => {
    try {
      return { intent: readPurchaseIntent(userId) };
    } catch (error) {
      return { error };
    }
  });
  const [intent, setIntent] = useState(initial.intent || null);
  const intentRef = useRef(initial.intent || null);
  const [form, setForm] = useState(freshForm);
  const [keyword, setKeyword] = useState('');
  const [creators, setCreators] = useState([]);
  const [creator, setCreator] = useState(null);
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState('');
  const [catalogVersion, setCatalogVersion] = useState(0);
  const [searching, setSearching] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [itemsError, setItemsError] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [review, setReview] = useState(initial.intent?.review || null);
  const [reviewInput, setReviewInput] = useState(null);
  const [error, setError] = useState(initial.error || null);
  const [recordedPurchase, setRecordedPurchase] = useState(null);
  const [statusRefresh, setStatusRefresh] = useState(0);
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const [now, setNow] = useState(Date.now());
  const [showNewWarning, setShowNewWarning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(!!initial.intent);
  const [showEvidence, setShowEvidence] = useState(false);
  const mounted = useRef(true);
  const item = items.find(value => value.id === itemId);
  const expired = review && Date.parse(review.previewExpiresAt) <= now;
  const frozen = !!intent;
  const quantityError =
    !frozen && item && form.quantity !== ''
      ? purchaseQuantityError(form.quantity, item)
      : '';

  let pricing = review;
  if (!pricing && !frozen) {
    try {
      pricing = calculatePurchasePrice(form, item);
    } catch (validationError) {
      // Keep the total empty until the pricing fields are complete and valid.
    }
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const id = intent?.id;
    if (!id) return undefined;
    let active = true;
    let timer;
    const startedAt = Date.now();
    const poll = async () => {
      let again = true;
      try {
        const result = await service.getPurchase(id);
        if (!active) return;
        setRecordedPurchase(result);
        setError(null);
        if (isPurchaseFulfilled(result)) {
          navigate(detailPath(id), {
            replace: true,
            state: {
              created: true,
              timeZone: intentRef.current?.timeZone,
              purchase: result,
            },
          });
        }
        again = !isPurchaseFulfilled(result) && result.status === 'processing';
      } catch (err) {
        if (!active) return;
        setError(err);
        again = ![400, 401, 403, 404].includes(err.status);
      }
      // Detect completion promptly while the administrator is waiting.
      if (active && again)
        timer = setTimeout(poll, Date.now() - startedAt < 60000 ? 2000 : 5000);
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [intent?.id, navigate, statusRefresh]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl]
  );

  useEffect(() => {
    let active = true;
    if (!keyword.trim() || creator || frozen) {
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const result = await service.searchCreators(keyword.trim());
        if (active) {
          setCreators(result);
          setSearchError(null);
        }
      } catch (err) {
        if (active) {
          setCreators([]);
          setSearchError(err);
        }
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [keyword, creator, frozen]);

  useEffect(() => {
    let active = true;
    if (!creator || frozen) {
      setItemsLoading(false);
      return undefined;
    }
    setItemsLoading(true);
    setItemsError(null);
    service
      .getItems(creator.id)
      .then(result => {
        if (active) setItems(result);
      })
      .catch(err => {
        if (active) {
          setItems([]);
          setItemsError(err);
        }
      })
      .finally(() => {
        if (active) setItemsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [creator, catalogVersion, frozen]);

  useEffect(() => {
    if (frozen) return;
    const discountValue = limitPurchaseDiscount(form.discountValue, form, item);
    if (discountValue !== form.discountValue) {
      setForm(previous => ({ ...previous, discountValue }));
      setReview(null);
      setReviewInput(null);
      setError(null);
    }
  }, [form, item, frozen]);

  const invalidateReview = () => {
    setReview(null);
    setReviewInput(null);
    setError(null);
  };
  const updateForm = (name, value) => {
    invalidateReview();
    setForm(previous => ({ ...previous, [name]: value }));
  };
  const startOperation = name => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(name);
    return true;
  };
  const endOperation = () => {
    busyRef.current = false;
    if (mounted.current) setBusy('');
  };
  const remember = next => {
    savePurchaseIntent(userId, next);
    intentRef.current = next;
    if (mounted.current) setIntent(next);
  };

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !startOperation('upload')) return;
    invalidateReview();
    setEvidence(null);
    setImageUrl('');
    setUploadError(null);
    try {
      validateEvidence(file);
      setImageUrl(URL.createObjectURL(file));
      const result = await service.uploadEvidence(file);
      if (mounted.current) setEvidence({ ...result, name: file.name });
    } catch (err) {
      if (mounted.current) setUploadError(err);
    } finally {
      endOperation();
    }
  };

  const preview = async event => {
    event?.preventDefault();
    if (!startOperation('preview')) return;
    setError(null);
    try {
      let payload;
      if (intentRef.current) {
        payload = { ...intentRef.current.payload };
        delete payload.idempotencyKey;
        delete payload.previewToken;
      } else {
        if (uploadError)
          throw new Error(
            'Remove the failed upload or upload evidence again before reviewing.'
          );
        payload = buildPurchaseInput(form, creator, item, evidence?.id);
      }
      const result = await service.preview(payload);
      if (intentRef.current) {
        remember({
          ...intentRef.current,
          review: result,
          needsReview: false,
          payload: {
            ...intentRef.current.payload,
            previewToken: result.previewToken,
          },
        });
      }
      if (mounted.current) {
        setReview(result);
        setReviewInput(payload);
        setNow(Date.now());
        setShowConfirm(true);
      }
    } catch (err) {
      if (mounted.current) {
        setError(err);
        setReview(null);
        if (
          !intentRef.current &&
          [
            'ManualPurchaseLimitReached',
            'ManualPurchaseItemUnavailable',
          ].includes(err.code)
        ) {
          setItemId('');
          setCatalogVersion(value => value + 1);
        }
      }
    } finally {
      endOperation();
    }
  };

  const confirm = async () => {
    if (
      !review ||
      intentRef.current?.needsReview ||
      (!intentRef.current && Date.parse(review.previewExpiresAt) <= Date.now())
    )
      return;
    if (!startOperation('confirm')) return;
    setError(null);
    try {
      if (!intentRef.current) {
        remember({
          review,
          timeZone: form.timeZone,
          evidenceName: evidence?.fileName || evidence?.name,
          payload: {
            ...reviewInput,
            idempotencyKey: crypto.randomUUID(),
            previewToken: review.previewToken,
          },
        });
      }
      const result = await service.confirm(intentRef.current.payload);
      const recorded = { ...intentRef.current, id: result.id };
      // If storage becomes unavailable after success, the original saved key still permits safe replay.
      try {
        remember(recorded);
      } catch (storageError) {
        intentRef.current = recorded;
        if (mounted.current) setIntent(recorded);
      }
      if (mounted.current) {
        setRecordedPurchase(result);
        if (isPurchaseFulfilled(result))
          navigate(detailPath(result.id), {
            replace: true,
            state: {
              created: true,
              timeZone: intentRef.current.timeZone,
              purchase: result,
            },
          });
      }
    } catch (err) {
      if (err.code === 'ManualPurchaseReviewChanged' && intentRef.current) {
        const next = { ...intentRef.current, needsReview: true };
        intentRef.current = next;
        if (mounted.current) setIntent(next);
        try {
          savePurchaseIntent(userId, next);
        } catch (storageError) {
          /* Preserve the in-memory intent. */
        }
      }
      if (mounted.current) setError(err);
    } finally {
      endOperation();
    }
  };

  const retryIssuance = async () => {
    if (!recordedPurchase?.retryable || !startOperation('retry')) return;
    setError(null);
    try {
      const result = await service.retry(intentRef.current.id);
      if (mounted.current) {
        setRecordedPurchase(result);
        setStatusRefresh(value => value + 1);
      }
    } catch (err) {
      if (mounted.current) setError(err);
    } finally {
      endOperation();
    }
  };

  const startNew = () => {
    try {
      clearPurchaseIntent(userId);
      intentRef.current = null;
      setIntent(null);
      setRecordedPurchase(null);
      setForm(freshForm());
      setKeyword('');
      setCreator(null);
      setCreators([]);
      setItems([]);
      setItemId('');
      setEvidence(null);
      setImageUrl('');
      setUploadError(null);
      setShowNewWarning(false);
      setShowConfirm(false);
      invalidateReview();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className='purchase-page space-y-[30px]'>
      <PurchaseHeader create />
      {!showConfirm && <PurchaseError error={error} />}
      {initial.error && !intent && (
        <p className='text-sm text-red-700'>
          Purchase recovery is unavailable in this browser session. Resolve the
          saved purchase before continuing.
        </p>
      )}
      {intent?.id && !showConfirm ? (
        <section className='rounded-xl border bg-white p-6 space-y-4'>
          <h2 className='text-xl font-semibold'>
            Your purchase has been recorded
          </h2>
          <p className='break-all text-sm text-gray-500'>
            Purchase ID: {intent.id}
          </p>
          <Link className='text-blue-700 underline' to={detailPath(intent.id)}>
            View purchase status
          </Link>
          <div>
            <button className={secondaryClass} onClick={startNew}>
              Create another
            </button>
          </div>
        </section>
      ) : (
        <div className='grid items-start gap-5 xl:grid-cols-[minmax(0,1.984fr)_minmax(0,1fr)]'>
          <form
            id='manual-purchase-form'
            onSubmit={preview}
            className='purchase-card rounded-[10px] border border-[#e8ebef] bg-white px-[19px] pt-[29px] pb-[30px]'
          >
            <h2 className='text-2xl font-semibold text-gray-900'>
              Purchase Details
            </h2>
            <p className='mt-[3px] mb-[30px] text-base text-gray-500'>
              One item type and one management reference per purchase.
            </p>
            {frozen ? (
              <div className='space-y-4'>
                <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'>
                  A confirmation was submitted. The saved details are locked so
                  retrying cannot create a second purchase. If the connection
                  was interrupted, retry the same confirmation to recover its
                  result.
                </div>
                {review && (
                  <PurchaseSummary
                    purchase={review}
                    timeZone={intent?.timeZone}
                    evidenceName={intent?.evidenceName}
                    onViewEvidence={
                      review.evidenceId
                        ? () => setShowEvidence(true)
                        : undefined
                    }
                  />
                )}
                <button
                  type='button'
                  className={secondaryClass}
                  disabled={!!busy}
                  onClick={() => setShowNewWarning(true)}
                >
                  Start a different purchase
                </button>
              </div>
            ) : (
              <fieldset
                disabled={!!busy || !!initial.error}
                className='grid gap-x-[15px] gap-y-[15px] sm:grid-cols-2'
              >
                <div className='relative min-w-0'>
                  <label
                    htmlFor='purchase-creator'
                    className='mb-px block text-base'
                  >
                    Creator <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='purchase-creator'
                    autoComplete='off'
                    className={`${inputClass} purchase-creator`}
                    title={creator?.id}
                    placeholder='Select creator name'
                    value={keyword}
                    onChange={event => {
                      invalidateReview();
                      setKeyword(event.target.value);
                      setCreator(null);
                      setCreators([]);
                      setItems([]);
                      setItemId('');
                      setSearchError(null);
                    }}
                  />
                  {searching && (
                    <p role='status' className='mt-2 text-sm text-gray-500'>
                      Searching creators…
                    </p>
                  )}
                  <PurchaseError error={searchError} />
                  {!creator &&
                    !searching &&
                    keyword.trim() &&
                    !searchError &&
                    !creators.length && (
                      <p className='mt-2 text-sm text-gray-500'>
                        No active creators found.
                      </p>
                    )}
                  {!!creators.length && !creator && (
                    <ul
                      aria-label='Creator search results'
                      className='absolute left-0 right-0 z-20 mt-2 max-h-60 overflow-y-auto rounded-lg border divide-y bg-white shadow-lg'
                    >
                      {creators.map(value => (
                        <li key={value.id}>
                          <button
                            type='button'
                            className='w-full px-3 py-3 text-left text-sm hover:bg-blue-50'
                            onClick={() => {
                              invalidateReview();
                              setCreator(value);
                              setKeyword(value.name);
                              setCreators([]);
                              setItems([]);
                              setItemId('');
                            }}
                          >
                            <span className='block font-medium'>
                              {value.name}
                            </span>
                            <span className='block text-xs text-gray-500 break-all'>
                              {value.id}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className='min-w-0'>
                  <label
                    htmlFor='purchase-item'
                    className='mb-px block text-base'
                  >
                    Item <span className='text-red-500'>*</span>
                  </label>
                  <select
                    id='purchase-item'
                    className={inputClass}
                    value={itemId}
                    disabled={!creator || itemsLoading || !!busy}
                    onChange={event => {
                      invalidateReview();
                      setItemId(event.target.value);
                    }}
                  >
                    <option value=''>
                      {itemsLoading ? 'Loading bundles…' : 'Select item'}
                    </option>
                    {items.map(value => (
                      <option
                        key={value.id}
                        value={value.id}
                        disabled={!value.purchasable || value.remaining < 1}
                      >
                        {value.name}
                        {!value.purchasable || value.remaining < 1
                          ? ' (unavailable)'
                          : ''}
                      </option>
                    ))}
                  </select>
                  {creator && !itemsLoading && !itemsError && !items.length && (
                    <p className='mt-2 text-sm text-gray-500'>
                      No ticket bundles are available for this creator.
                    </p>
                  )}
                  <PurchaseError error={itemsError} />
                  {itemsError && (
                    <button
                      type='button'
                      className='mt-2 text-sm text-blue-700'
                      onClick={() => setCatalogVersion(value => value + 1)}
                    >
                      Reload bundles
                    </button>
                  )}
                </div>
                <div>
                  <label
                    htmlFor='purchase-unit-price'
                    className='mb-px block text-base'
                  >
                    Unit Price <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='purchase-unit-price'
                    className={`${inputClass} bg-gray-100 text-gray-500`}
                    readOnly
                    value={item ? formatPurchaseMoney(item.unitPrice) : '€0.00'}
                  />
                </div>
                <div>
                  <label
                    htmlFor='purchase-quantity'
                    className='mb-px block text-base'
                  >
                    Quantity <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='purchase-quantity'
                    className={inputClass}
                    type='number'
                    min='1'
                    max={Math.min(10000, item?.remaining ?? 10000)}
                    step='1'
                    required
                    aria-invalid={!!quantityError}
                    aria-describedby={
                      quantityError ? 'purchase-quantity-error' : undefined
                    }
                    value={form.quantity}
                    placeholder='Enter quantity'
                    onChange={event =>
                      updateForm('quantity', event.target.value)
                    }
                  />
                  {quantityError && (
                    <p
                      id='purchase-quantity-error'
                      role='alert'
                      className='mt-2 text-sm text-red-600'
                    >
                      {quantityError}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor='purchase-discount'
                    className='mb-px block text-base'
                  >
                    Discount Type (Optional)
                  </label>
                  <select
                    id='purchase-discount'
                    className={inputClass}
                    style={
                      !form.discountType ? { color: '#6b7281' } : undefined
                    }
                    value={form.discountType}
                    onChange={event => {
                      const discountType = event.target.value;
                      invalidateReview();
                      setForm(previous => ({
                        ...previous,
                        discountType,
                        discountValue: '',
                      }));
                    }}
                  >
                    <option value='' disabled>
                      Select discount type
                    </option>
                    <option value='none'>No discount</option>
                    <option value='percentage'>Percentage</option>
                    <option value='fixed'>Fixed amount (EUR)</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor='purchase-discount-value'
                    className='mb-px block text-base'
                  >
                    Discount Value (Optional)
                  </label>
                  <div className='relative'>
                    <input
                      id='purchase-discount-value'
                      className={inputClass}
                      type='text'
                      inputMode='decimal'
                      disabled={!!busy || form.discountType === 'none'}
                      value={
                        form.discountType === 'none' ? '' : form.discountValue
                      }
                      placeholder='Enter discount value'
                      onChange={event =>
                        updateForm(
                          'discountValue',
                          limitPurchaseDiscount(event.target.value, form, item)
                        )
                      }
                    />
                    {form.discountType === 'percentage' &&
                      form.discountValue && (
                        <span
                          aria-hidden='true'
                          className='pointer-events-none absolute inset-0 flex items-center overflow-hidden px-[15px]'
                        >
                          <span className='invisible shrink-0 whitespace-pre'>
                            {form.discountValue}
                          </span>
                          <span className='shrink-0'>%</span>
                        </span>
                      )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor='purchase-total-price'
                    className='mb-px block text-base'
                  >
                    Total Price <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='purchase-total-price'
                    className={`${inputClass} bg-gray-100 text-gray-500`}
                    readOnly
                    value={pricing ? formatPurchaseMoney(pricing.total) : ''}
                    placeholder='€0.00'
                  />
                </div>
                <div>
                  <label
                    htmlFor='purchase-reference'
                    className='mb-px block text-base'
                  >
                    Reference <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='purchase-reference'
                    className={inputClass}
                    required
                    maxLength={255}
                    value={form.reference}
                    placeholder='Enter reference'
                    onChange={event =>
                      updateForm('reference', event.target.value)
                    }
                  />
                </div>
                <PaymentDateTime
                  form={form}
                  now={now}
                  onChange={values => {
                    invalidateReview();
                    setForm(previous => ({ ...previous, ...values }));
                  }}
                />
                <div className='sm:col-span-2 space-y-2'>
                  <label
                    htmlFor='purchase-evidence'
                    className='block text-base'
                  >
                    Evidence Image (Optional)
                  </label>
                  <div className='flex flex-wrap items-center gap-2 pt-2'>
                    {imageUrl ? (
                      <button
                        type='button'
                        className='max-w-full break-all text-left text-[#8a8c9f] hover:underline'
                        onClick={() => setShowEvidence(true)}
                      >
                        {evidence?.fileName ||
                          evidence?.name ||
                          'Selected image'}
                      </button>
                    ) : (
                      <span className='text-[#8a8c9f]'>
                        Recommended: PNG, WebP, JPG.
                      </span>
                    )}
                    <label
                      className={`cursor-pointer font-semibold text-blue-600 underline ${
                        busy ? 'opacity-50' : ''
                      }`}
                    >
                      Upload
                      <input
                        id='purchase-evidence'
                        type='file'
                        accept='image/jpeg,image/png,image/webp'
                        onChange={upload}
                        className='sr-only'
                        aria-label='Upload payment evidence'
                      />
                    </label>
                  </div>
                  {busy === 'upload' && (
                    <p role='status' className='text-sm text-gray-500'>
                      Uploading evidence…
                    </p>
                  )}
                  <PurchaseError error={uploadError} />
                  {uploadError && (
                    <button
                      type='button'
                      className='block text-sm text-blue-700 underline'
                      onClick={() => {
                        invalidateReview();
                        setEvidence(null);
                        setImageUrl('');
                        setUploadError(null);
                      }}
                    >
                      Remove evidence and continue without it
                    </button>
                  )}
                </div>
              </fieldset>
            )}
            {showNewWarning && (
              <div
                role='alert'
                className='mt-5 rounded-lg border border-amber-300 p-4 space-y-3 text-sm'
              >
                <p>
                  The previous payment may already be recorded. Starting a new
                  purchase does not cancel it and can create a duplicate if you
                  enter the same payment again. Verify its outcome first.
                </p>
                <div className='flex flex-wrap gap-3'>
                  <button
                    type='button'
                    className={secondaryClass}
                    onClick={() => setShowNewWarning(false)}
                  >
                    Keep current purchase
                  </button>
                  <button
                    type='button'
                    className={primaryClass}
                    onClick={startNew}
                  >
                    Start new purchase
                  </button>
                </div>
              </div>
            )}
          </form>
          <aside className='space-y-[30px]'>
            <OrderSummary
              item={frozen ? review?.item : item}
              quantity={frozen ? review?.quantity : form.quantity}
              pricing={pricing}
              validationMessage={quantityError}
            />
            <div className='purchase-management rounded-xl border border-[#dee9f4] bg-[#f0f6ff] px-[19px] py-[9px] text-sm'>
              <h3 className='text-[14px] leading-[22px] tracking-[0.1px] font-semibold text-[#004fe0]'>
                Management Confirmation
              </h3>
              <p className='mt-[10px] text-[#6b7281] tracking-[0.17px]'>
                Confirm the agreed total and any funds received before
                continuing.
              </p>
            </div>
            {frozen && review && !intent.needsReview ? (
              <button
                type='button'
                className={`${primaryClass} w-full`}
                disabled={!!busy}
                onClick={() => setShowConfirm(true)}
              >
                Review saved confirmation
              </button>
            ) : (
              <button
                type='submit'
                form='manual-purchase-form'
                className={`${primaryClass} w-full`}
                disabled={
                  !!busy ||
                  !!initial.error ||
                  (!frozen &&
                    (!creator ||
                      !item ||
                      !!uploadError ||
                      itemsLoading ||
                      !!quantityError))
                }
              >
                {busy === 'preview'
                  ? 'Preparing review…'
                  : frozen
                  ? 'Refresh review for this purchase'
                  : 'Continue to Confirm'}
              </button>
            )}
          </aside>
        </div>
      )}
      <PurchaseModal
        open={showConfirm && !!review}
        title='Confirm Manual Purchase'
        subtitle='Review the details before creating the purchase.'
        busy={!!busy}
        onClose={() => {
          if (intent?.id) navigate(ROUTES.PURCHASES);
          else setShowConfirm(false);
        }}
        footer={
          <>
            <button
              type='button'
              className={`${secondaryClass} ${
                !frozen ? 'w-[120px] !px-0' : ''
              }`}
              disabled={!!busy}
              onClick={() => {
                if (intent?.id) navigate(ROUTES.PURCHASES);
                else setShowConfirm(false);
              }}
            >
              {intent?.id
                ? 'Back to Purchases'
                : frozen
                ? 'Back to Purchase'
                : 'Back to Edit'}
            </button>
            {intent?.id ? (
              !isPurchaseFulfilled(recordedPurchase) &&
              recordedPurchase?.status === 'failed' ? (
                recordedPurchase.retryable && (
                  <button
                    type='button'
                    className={primaryClass}
                    disabled={!!busy}
                    onClick={retryIssuance}
                  >
                    {busy === 'retry'
                      ? 'Retrying ticket issuance…'
                      : 'Retry ticket issuance'}
                  </button>
                )
              ) : (
                <button
                  type='button'
                  className={primaryClass}
                  disabled={!!busy || !error}
                  aria-busy={!error}
                  aria-label={
                    error ? 'Refresh purchase status' : 'Processing purchase…'
                  }
                  onClick={() => setStatusRefresh(value => value + 1)}
                >
                  <span role='status'>
                    {error ? 'Refresh purchase status' : 'Processing purchase…'}
                  </span>
                </button>
              )
            ) : intent?.needsReview ? (
              <button
                type='button'
                className={primaryClass}
                disabled={!!busy}
                onClick={preview}
              >
                Refresh review for this purchase
              </button>
            ) : (
              <button
                type='button'
                className={`${primaryClass} ${
                  !frozen && busy !== 'confirm' ? 'w-[163px] !px-0' : ''
                }`}
                disabled={!!busy || (!frozen && expired) || !!initial.error}
                onClick={confirm}
              >
                {busy === 'confirm'
                  ? 'Recording purchase…'
                  : frozen
                  ? 'Retry same confirmation'
                  : 'Confirm Purchase'}
              </button>
            )}
          </>
        }
      >
        <PurchaseError
          error={
            error ||
            (!isPurchaseFulfilled(recordedPurchase) &&
            recordedPurchase?.status === 'failed'
              ? {
                  message: recordedPurchase.retryable
                    ? 'Ticket issuance failed. Retry ticket issuance to complete this purchase.'
                    : 'This purchase could not be completed. Contact support for assistance.',
                }
              : null)
          }
        />
        {review && (
          <PurchaseSummary
            purchase={review}
            timeZone={intent?.timeZone || form.timeZone}
            evidenceName={
              evidence?.fileName || evidence?.name || intent?.evidenceName
            }
            onViewEvidence={
              imageUrl || review.evidenceId
                ? () => setShowEvidence(true)
                : undefined
            }
          />
        )}
        {intent?.needsReview && (
          <p role='status' className='text-sm text-amber-800'>
            The review changed. Refresh and review the latest details before
            confirming again.
          </p>
        )}
        {expired && !frozen && (
          <p role='status' className='text-sm text-amber-800'>
            This review has expired. Go back and continue to confirm again to
            refresh it.
          </p>
        )}
        <div className='rounded-xl border border-[#dee9f4] bg-[#f0f6ff] px-[19px] py-[9px] text-sm text-gray-500'>
          Final total drives commission. Confirming creates the item and VP.
          This flow has no cancellation action.
        </div>
      </PurchaseModal>
      <EvidenceLightbox
        open={showEvidence && !!imageUrl}
        onClose={() => setShowEvidence(false)}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt='Selected payment evidence'
            className='h-full w-full object-contain'
          />
        )}
      </EvidenceLightbox>
      <EvidenceViewer
        evidenceId={review?.evidenceId}
        open={showEvidence && !imageUrl && !!review?.evidenceId}
        onClose={() => setShowEvidence(false)}
      />
    </div>
  );
};

const ManualPurchasePage = () => {
  const { user } = useAuth();
  return <ManualPurchaseForm key={user.id} userId={user.id} />;
};

export default ManualPurchasePage;
